import React from "react";
import ReactMarkdown from "react-markdown";

interface Props {
  content?: string;
  emptyLabel?: string;
  className?: string;
}

export function MarkdownPreview({
  content,
  emptyLabel = "Nothing to preview yet.",
  className = "",
}: Props) {
  const trimmedContent = content?.trim() || "";
  const normalizedContent = normalizeMarkdownContent(trimmedContent);

  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 ${className}`.trim()}>
      {trimmedContent ? (
        <div className="markdown-body max-w-none text-xs text-slate-800">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
              ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
              a: ({ children, href }) => (
                <a href={href} className="text-indigo-600 underline underline-offset-2">
                  {children}
                </a>
              ),
            }}
          >
            {normalizedContent}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="text-xs italic text-slate-400">{emptyLabel}</p>
      )}
    </div>
  );
}

export function normalizeMarkdownContent(content?: string) {
  const trimmedContent = content?.trim() || "";
  const baseLines = trimmedContent.split("\n").map((line) => {
    const trimmedLine = line.trimStart();
    if (/^[•·●]\s*/.test(trimmedLine)) {
      const indent = line.slice(0, line.length - trimmedLine.length);
      return `${indent}- ${trimmedLine.replace(/^[•·●]\s*/, "")}`;
    }
    if (/^-(\S)/.test(trimmedLine)) {
      const indent = line.slice(0, line.length - trimmedLine.length);
      return `${indent}- ${trimmedLine.slice(1).trimStart()}`;
    }
    if (/^\*(\S)/.test(trimmedLine)) {
      const indent = line.slice(0, line.length - trimmedLine.length);
      return `${indent}* ${trimmedLine.slice(1).trimStart()}`;
    }
    return line;
  });

  const hasBulletStart = baseLines.some((line) => /^\s*[-*]\s+/.test(line));
  const hasBlankLines = baseLines.some((line) => line.trim() === "");

  if (hasBulletStart && !hasBlankLines) {
    let currentMarker: "-" | "*" | null = null;
    const normalizedLines = baseLines.map((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        currentMarker = null;
        return line;
      }
      const explicitMarker = trimmedLine.match(/^([-*])\s+/)?.[1] as "-" | "*" | undefined;
      if (explicitMarker) {
        currentMarker = explicitMarker;
        return line;
      }
      if (currentMarker) {
        const leadingWhitespace = line.match(/^\s*/)?.[0] || "";
        return `${leadingWhitespace}${currentMarker} ${trimmedLine}`;
      }
      return line;
    });
    return normalizedLines.join("\n");
  }

  return baseLines.join("\n");
}
